import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFloatMessage, MESSAGE_TYPE } from '../../../../components/Providers/FloatMessage';
import {
    GetSuggestions,
    GetActivePlaylist,
    GetMetrics,
    GetSettings,
    TogglePause,
    SetScreenMode,
    ResetDynamic,
    ExportPlaylistToExcel
} from '../../../../utilities/BL/Admin/Overlays/Playlist';
import {
    ConnectWebSocketForPlaylistAdmin,
    DisconnectWebSocketForPlaylistAdmin
} from '../../../../utilities/WS/Admin/PlaylistWS';

import { PlaylistManualSongModal } from './PlaylistManualSongModal';
import { PlaylistEditSongModal } from './PlaylistEditSongModal';
import { PlaylistSuggestionsTable } from './PlaylistSuggestionsTable';
import { PlaylistActiveList } from './PlaylistActiveList';
import { PlaylistSettingsModal } from './PlaylistSettingsModal';
import { PlaylistThemeModal } from './PlaylistThemeModal';
import { PlaylistResetConfirmModal } from './PlaylistResetConfirmModal';

/**
 * Consola Administrativa Full-Screen para Cabina de Audio / DJ - Dinámica "Arma la Playlist" (Tile ID 46).
 * Integra layout dual (50/50 split), métricas en tiempo real, control de pantalla, modales y sincronización WebSocket.
 * 
 * @component PlaylistSpecialBoard
 * @param {Object} props
 * @param {Function} [props.getOutOfTheDynamic] - Callback para salir al Dashboard administrativo.
 */
export const PlaylistSpecialBoard = ({ getOutOfTheDynamic }) => {
    const navigate = useNavigate();
    const { showMessage } = useFloatMessage();

    // Listas y estados principales
    const [suggestions, setSuggestions] = useState([]);
    const [activePlaylist, setActivePlaylist] = useState([]);
    const [metrics, setMetrics] = useState({
        totalSuggestions: 0,
        activeInPlaylist: 0,
        totalPlayed: 0,
        totalOperatorAdded: 0,
        uniqueFans: 0,
        topRequestedSongTitle: '',
        topRequestedSongVotes: 0
    });
    const [settings, setSettings] = useState({
        screenMode: 0,
        isSubmissionActive: true,
        submissionPausedMessage: '',
        cooldownSeconds: 120,
        currentPlayingSongId: null
    });

    // Estados operativos
    const [isLoading, setIsLoading] = useState(false);
    const [groupSimilar, setGroupSimilar] = useState(false);
    const [webSocketStatus, setWebSocketStatus] = useState('connecting');

    // Modales
    const [showManualModal, setShowManualModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [showThemeModal, setShowThemeModal] = useState(false);
    const [songToEdit, setSongToEdit] = useState(null);
    const [showResetModal, setShowResetModal] = useState(false);

    // Carga general de datos
    const loadAllData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [sugRes, activeRes, metricsRes, settingsRes] = await Promise.all([
                GetSuggestions({ includeArchived: true, groupSimilar }),
                GetActivePlaylist(),
                GetMetrics(),
                GetSettings()
            ]);

            if (sugRes && sugRes.success && Array.isArray(sugRes.data)) {
                setSuggestions(sugRes.data);
            }
            if (activeRes && activeRes.success && Array.isArray(activeRes.data)) {
                setActivePlaylist(activeRes.data);
            }
            if (metricsRes && metricsRes.success && metricsRes.data) {
                setMetrics(metricsRes.data);
            }
            if (settingsRes && settingsRes.success && settingsRes.data) {
                setSettings(settingsRes.data);
            }
        } catch (err) {
            console.error('Error al sincronizar datos de cabina DJ:', err);
        } finally {
            setIsLoading(false);
        }
    }, [groupSimilar]);

    // Manejador de eventos en tiempo real WebSocket
    const handleWebSocketPacket = useCallback((packet) => {
        if (!packet || typeof packet !== 'object') return;

        const action = packet.action;
        const data = packet.data;

        switch (action) {
            case 'suggestion_received':
                if (data) {
                    setSuggestions(prev => {
                        const exists = prev.some(s => s.id === data.id);
                        if (exists) return prev.map(s => s.id === data.id ? data : s);
                        return [data, ...prev];
                    });
                    setMetrics(prev => ({
                        ...prev,
                        totalSuggestions: prev.totalSuggestions + 1
                    }));
                }
                break;
            case 'vote_added':
                if (data && data.suggestionId) {
                    setSuggestions(prev => prev.map(s => {
                        if (s.id === data.suggestionId) {
                            return { ...s, voteCount: data.newVoteCount };
                        }
                        return s;
                    }));
                }
                break;
            case 'suggestion_updated':
                if (data) {
                    setSuggestions(prev => prev.map(s => s.id === data.id ? data : s));
                    setActivePlaylist(prev => prev.map(s => s.id === data.id ? data : s));
                }
                break;
            case 'suggestion_archived':
                if (data && data.suggestionId) {
                    setSuggestions(prev => prev.map(s => s.id === data.suggestionId ? { ...s, isArchived: data.isArchived } : s));
                }
                break;
            case 'suggestion_deleted':
                if (data && data.suggestionId) {
                    setSuggestions(prev => prev.filter(s => s.id !== data.suggestionId));
                    setActivePlaylist(prev => prev.filter(s => s.id !== data.suggestionId));
                }
                break;
            case 'playlist_updated':
                if (Array.isArray(data)) {
                    setActivePlaylist(data);
                }
                break;
            case 'now_playing_changed':
                if (data) {
                    const currentId = data.currentSong ? data.currentSong.id : null;
                    setSettings(prev => ({ ...prev, currentPlayingSongId: currentId }));
                    setActivePlaylist(prev => prev.map(s => ({
                        ...s,
                        isNowPlaying: s.id === currentId
                    })));
                }
                break;
            case 'metrics_updated':
                if (data) {
                    setMetrics(data);
                }
                break;
            case 'settings_updated':
                if (data) {
                    setSettings(prev => ({ ...prev, ...data }));
                }
                break;
            case 'dynamic_reset':
                setSuggestions([]);
                setActivePlaylist([]);
                setMetrics({
                    totalSuggestions: 0,
                    activeInPlaylist: 0,
                    totalPlayed: 0,
                    totalOperatorAdded: 0,
                    uniqueFans: 0,
                    topRequestedSongTitle: '',
                    topRequestedSongVotes: 0
                });
                break;
            default:
                break;
        }
    }, []);

    // Inicialización y ciclo de vida de WebSockets
    useEffect(() => {
        loadAllData();

        ConnectWebSocketForPlaylistAdmin(
            handleWebSocketPacket,
            (status) => setWebSocketStatus(status),
            'superadmin'
        );

        return () => {
            DisconnectWebSocketForPlaylistAdmin();
        };
    }, [loadAllData, handleWebSocketPacket]);

    // Pausar / Reanudar sugerencias de aficionados
    const handleTogglePause = async () => {
        const nextState = !settings.isSubmissionActive;
        try {
            const res = await TogglePause(nextState);
            if (res && res.success) {
                setSettings(prev => ({ ...prev, isSubmissionActive: nextState }));
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: nextState
                        ? '🟢 Recepción de sugerencias ACTIVADA.'
                        : '⏸️ Recepción de sugerencias PAUSADA para aficionados.',
                    message: nextState
                        ? '🟢 Recepción de sugerencias ACTIVADA.'
                        : '⏸️ Recepción de sugerencias PAUSADA para aficionados.'
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: (res && res.message) || 'Error al cambiar estado de recepción.',
                    message: (res && res.message) || 'Error al cambiar estado de recepción.'
                });
            }
        } catch (err) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de red al alternar pausa.',
                message: 'Error de red al alternar pausa.'
            });
        }
    };

    // Cambiar modo de pantalla o proyección
    const handleScreenModeChange = async (mode) => {
        try {
            const res = await SetScreenMode(mode);
            if (res && res.success) {
                setSettings(prev => ({ ...prev, screenMode: mode }));
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `Modo de pantalla cambiado a: ${mode === 0 ? 'QR / Llamado' : mode === 1 ? 'Now Playing' : 'Top Queue'}.`,
                    message: `Modo de pantalla cambiado a: ${mode === 0 ? 'QR / Llamado' : mode === 1 ? 'Now Playing' : 'Top Queue'}.`
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: (res && res.message) || 'Error al cambiar modo de pantalla.',
                    message: (res && res.message) || 'Error al cambiar modo de pantalla.'
                });
            }
        } catch (err) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al actualizar pantalla.',
                message: 'Error de conexión al actualizar pantalla.'
            });
        }
    };

    // Exportar CSV
    const handleExport = async () => {
        try {
            const res = await ExportPlaylistToExcel();
            if (res && res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: 'Descarga del reporte de Playlist iniciada con éxito.',
                    message: 'Descarga del reporte de Playlist iniciada con éxito.'
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: (res && res.message) || 'Error al generar el archivo.',
                    message: (res && res.message) || 'Error al generar el archivo.'
                });
            }
        } catch (err) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de red al exportar archivo.',
                message: 'Error de red al exportar archivo.'
            });
        }
    };

    // Salir al Dashboard
    const handleExit = () => {
        if (getOutOfTheDynamic && typeof getOutOfTheDynamic === 'function') {
            getOutOfTheDynamic();
        } else {
            navigate('/admin/dashboard');
        }
    };

    // Abrir modal de edición
    const handleOpenEdit = (song) => {
        setSongToEdit(song);
        setShowEditModal(true);
    };

    return (
        <main className="PlaylistSpecialBoard">
            {/* Header Principal de la Cabina */}
            <header className="PlaylistSpecialBoard__Header">
                <div className="PlaylistSpecialBoard__HeaderBrand">
                    <button
                        type="button"
                        className="PlaylistSpecialBoard__ExitBtn"
                        onClick={handleExit}
                        title="Volver al Dashboard"
                    >
                        <i className="bi bi-arrow-left me-1"></i> Dashboard
                    </button>

                    <div className="PlaylistSpecialBoard__TitleBox">
                        <div className="d-flex align-items-center gap-2">
                            <span className="PlaylistSpecialBoard__LogoIcon">
                                <i className="bi bi-music-note-list"></i>
                            </span>
                            <h1 className="PlaylistSpecialBoard__MainTitle mb-0">
                                Cabina de Audio • Arma la Playlist
                            </h1>
                        </div>
                        <p className="PlaylistSpecialBoard__Subtitle mb-0">
                            Control DJ y moderación en tiempo real para estadio
                        </p>
                    </div>
                </div>

                {/* Controles de Cabina y Telemetría */}
                <div className="PlaylistSpecialBoard__HeaderControls">
                    {/* Indicador de Conexión WebSocket */}
                    <div
                        className={`PlaylistSpecialBoard__StatusBadge ${
                            webSocketStatus === 'connected'
                                ? 'PlaylistSpecialBoard__StatusBadge--connected'
                                : 'PlaylistSpecialBoard__StatusBadge--reconnecting'
                        }`}
                    >
                        <span className="PlaylistSpecialBoard__StatusDot"></span>
                        {webSocketStatus === 'connected' ? 'En Vivo (WebSocket)' : 'Reconectando...'}
                    </div>

                    {/* Botón Pausa / Reanudar */}
                    <button
                        type="button"
                        className={`PlaylistSpecialBoard__Btn ${
                            settings.isSubmissionActive
                                ? 'PlaylistSpecialBoard__Btn--pause'
                                : 'PlaylistSpecialBoard__Btn--resume'
                        }`}
                        onClick={handleTogglePause}
                    >
                        <i className={`bi ${settings.isSubmissionActive ? 'bi-pause-circle' : 'bi-play-circle'} me-1`}></i>
                        {settings.isSubmissionActive ? 'Pausar Sugerencias' : 'Reanudar Sugerencias'}
                    </button>

                    {/* Botón Destacado + Agregar Canción */}
                    <button
                        type="button"
                        className="PlaylistSpecialBoard__Btn PlaylistSpecialBoard__Btn--primary"
                        onClick={() => setShowManualModal(true)}
                    >
                        <i className="bi bi-plus-circle-fill me-1"></i> + Agregar Canción
                    </button>

                    {/* Botón Personalizar Tema 🎨 */}
                    <button
                        type="button"
                        className="PlaylistSpecialBoard__Btn PlaylistSpecialBoard__Btn--secondary"
                        onClick={() => setShowThemeModal(true)}
                        title="Personalizar tema visual y fondos (SkiaSharp)"
                    >
                        <i className="bi bi-palette-fill me-1 text-danger"></i> Tema 🎨
                    </button>

                    {/* Botón Configuración ⚙️ */}
                    <button
                        type="button"
                        className="PlaylistSpecialBoard__Btn PlaylistSpecialBoard__Btn--secondary"
                        onClick={() => setShowSettingsModal(true)}
                        title="Configuración operativa, cooldown y pantalla"
                    >
                        <i className="bi bi-gear-fill me-1 text-primary"></i> Ajustes ⚙️
                    </button>

                    {/* Botón Exportar CSV */}
                    <button
                        type="button"
                        className="PlaylistSpecialBoard__Btn PlaylistSpecialBoard__Btn--secondary"
                        onClick={handleExport}
                        title="Exportar reporte CSV"
                    >
                        <i className="bi bi-file-earmark-excel me-1"></i> Exportar
                    </button>

                    {/* Botón Purgar */}
                    <button
                        type="button"
                        className="PlaylistSpecialBoard__Btn PlaylistSpecialBoard__Btn--danger"
                        onClick={() => setShowResetModal(true)}
                        title="Purgar dinámica (Zona de Peligro)"
                    >
                        <i className="bi bi-trash"></i>
                    </button>
                </div>
            </header>

            {/* Fila Superior de Métricas & Control de Pantalla */}
            <div className="PlaylistSpecialBoard__TopBar">
                {/* 5 KPIs de Telemetría */}
                <div className="PlaylistSpecialBoard__MetricsGrid">
                    <div className="PlaylistSpecialBoard__MetricCard">
                        <span className="PlaylistSpecialBoard__MetricLabel">Total Sugerencias</span>
                        <span className="PlaylistSpecialBoard__MetricValue">{metrics.totalSuggestions}</span>
                    </div>

                    <div className="PlaylistSpecialBoard__MetricCard PlaylistSpecialBoard__MetricCard--active">
                        <span className="PlaylistSpecialBoard__MetricLabel">En Playlist Activa</span>
                        <span className="PlaylistSpecialBoard__MetricValue">{metrics.activeInPlaylist}</span>
                    </div>

                    <div className="PlaylistSpecialBoard__MetricCard PlaylistSpecialBoard__MetricCard--played">
                        <span className="PlaylistSpecialBoard__MetricLabel">Reproducidas</span>
                        <span className="PlaylistSpecialBoard__MetricValue">{metrics.totalPlayed}</span>
                    </div>

                    <div className="PlaylistSpecialBoard__MetricCard">
                        <span className="PlaylistSpecialBoard__MetricLabel">Fans Únicos</span>
                        <span className="PlaylistSpecialBoard__MetricValue">{metrics.uniqueFans}</span>
                    </div>

                    <div className="PlaylistSpecialBoard__MetricCard PlaylistSpecialBoard__MetricCard--top">
                        <span className="PlaylistSpecialBoard__MetricLabel">Más Pedida</span>
                        <span className="PlaylistSpecialBoard__MetricTopTitle text-truncate" title={metrics.topRequestedSongTitle || 'Sin votos'}>
                            {metrics.topRequestedSongTitle ? `🔥 ${metrics.topRequestedSongTitle}` : 'N/A'}
                        </span>
                    </div>
                </div>

                {/* Switch de Modo de Pantalla Estadio */}
                <div className="PlaylistSpecialBoard__ScreenModeBox">
                    <span className="PlaylistSpecialBoard__ScreenModeLabel">
                        <i className="bi bi-display me-1 text-danger"></i> Pantalla:
                    </span>
                    <div className="PlaylistSpecialBoard__ScreenModeGroup">
                        <button
                            type="button"
                            className={`PlaylistSpecialBoard__ScreenModeBtn ${Number(settings.screenMode) === 0 ? 'PlaylistSpecialBoard__ScreenModeBtn--active' : ''}`}
                            onClick={() => handleScreenModeChange(0)}
                        >
                            QR / CTA
                        </button>
                        <button
                            type="button"
                            className={`PlaylistSpecialBoard__ScreenModeBtn ${Number(settings.screenMode) === 1 ? 'PlaylistSpecialBoard__ScreenModeBtn--active' : ''}`}
                            onClick={() => handleScreenModeChange(1)}
                        >
                            🎵 Now Playing
                        </button>
                        <button
                            type="button"
                            className={`PlaylistSpecialBoard__ScreenModeBtn ${Number(settings.screenMode) === 2 ? 'PlaylistSpecialBoard__ScreenModeBtn--active' : ''}`}
                            onClick={() => handleScreenModeChange(2)}
                        >
                            📋 Top Queue
                        </button>
                    </div>
                </div>
            </div>

            {/* Layout Dual 50/50 con Scrollbars Independientes */}
            <div className="PlaylistSpecialBoard__DualWorkspace">
                {/* Panel Izquierdo: Buzón de Sugerencias */}
                <div className="PlaylistSpecialBoard__LeftColumn">
                    <PlaylistSuggestionsTable
                        suggestions={suggestions}
                        isLoading={isLoading}
                        groupSimilar={groupSimilar}
                        onToggleGroupSimilar={(val) => setGroupSimilar(val)}
                        onEdit={handleOpenEdit}
                        onRefresh={loadAllData}
                    />
                </div>

                {/* Panel Derecho: Playlist Activa del Estadio */}
                <div className="PlaylistSpecialBoard__RightColumn">
                    <PlaylistActiveList
                        activePlaylist={activePlaylist}
                        currentPlayingSongId={settings.currentPlayingSongId}
                        onRefresh={loadAllData}
                        onPlaylistChange={setActivePlaylist}
                    />
                </div>
            </div>

            {/* MODALES REUTILIZABLES */}
            <PlaylistManualSongModal
                isOpen={showManualModal}
                onClose={() => setShowManualModal(false)}
                onSuccess={loadAllData}
            />

            <PlaylistEditSongModal
                isOpen={showEditModal}
                song={songToEdit}
                onClose={() => {
                    setShowEditModal(false);
                    setSongToEdit(null);
                }}
                onSuccess={loadAllData}
            />

            <PlaylistSettingsModal
                isOpen={showSettingsModal}
                onClose={() => setShowSettingsModal(false)}
                onSuccess={loadAllData}
            />

            <PlaylistThemeModal
                isOpen={showThemeModal}
                onClose={() => setShowThemeModal(false)}
                onSuccess={loadAllData}
            />

            <PlaylistResetConfirmModal
                isOpen={showResetModal}
                onClose={() => setShowResetModal(false)}
                onSuccess={loadAllData}
            />
        </main>
    );
};

export default PlaylistSpecialBoard;
